import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { faDownload } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { Avatar } from '@components/basics/Images/Avatar';
import { Cover } from '@components/basics/Images/Cover';
import { Button } from '@components/main-components/button/Button';
import { Divider } from '@components/main-components/divider';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { DeprecatedDynamicSkeleton } from '@components/main-components/skeleton/Skeleton';
import { MediaQuery } from '@context/mediaQuery';
import { ModalContext } from '@context/modal';
import type {
  ReleaseAllowlistSelectedArtistsQuery,
  ReleaseAllowlistSelectedReleasesQuery,
} from '@graphql/reactQuery';
import type { CursorConnectionArgs } from '@graphql/reactQuery';
import {
  BaseReleaseAllowlistDocument,
  ReleaseAllowlistSelectedArtistsDocument,
  ReleaseAllowlistSelectedReleasesDocument,
  useQuery,
} from '@graphql/reactQuery';
import {
  GetReleaseAllowListedAddressesCsvUrlDocument,
  MintingAccessConfigMintingType,
  ReleaseAllowlistModalDataDocument,
} from '@graphql/reactQuery';
import { getAllowlistDisplayName } from '@utils/constants';
import { getStaticCoverImage } from '@utils/helpers';
import { mergeUniqLists } from '@utils/mergeUniqueLists';
import { AllArtistCollaborationsRow } from './AllArtistCollaborationsRow';
import { AllArtistCollectorsRow } from './AllArtistCollectorsRow';
import { ArtistRowSkeleton } from './ArtistRowSkeleton';
import { ReleaseRowSkeleton } from './ReleaseRowSkeleton';
import {
  CollectorAndReleaseDetails,
  CollectorRows,
  Header,
  MANUALLY_ADDED_ADDRESSES_HEADER,
  Row,
  RowSubTitle,
  RowTitle,
  Section,
  SectionHeader,
  Sections,
  SELECTED_ARTISTS_COLLECTORS_HEADER,
  SELECTED_RELEASES_COLLECTORS_HEADER,
  SubText,
} from './shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  query BaseReleaseAllowlist($input: ReleaseAllowlistInput!) {
    releaseAllowlist(input: $input) {
      id
      createdAt
      info {
        allArtistCollaborations
        allArtistCollectors
      }
      totalUsers
      manuallyAddedAddresses {
        id
        totalAddresses
        description
      }
    }
  }

  query ReleaseAllowlistSelectedReleases(
    $input: ReleaseAllowlistInput!
    $pagination: CursorConnectionArgs!
  ) {
    releaseAllowlist(input: $input) {
      id
      selectedReleaseSources(pagination: $pagination) {
        edges {
          node {
            id
            sourceRelease {
              id
              coverImage {
                id
                url
              }
              staticCoverImage {
                id
                url
              }
              title
              artist {
                id
                name
              }
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }

  query ReleaseAllowlistSelectedArtists(
    $input: ReleaseAllowlistInput!
    $pagination: CursorConnectionArgs!
  ) {
    releaseAllowlist(input: $input) {
      id
      selectedArtistSources(pagination: $pagination) {
        edges {
          node {
            id
            sourceArtist {
              id
              name
              user {
                id
                publicAddress
                avatar {
                  id
                  url
                }
                username
              }
              webappUri
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }

  query GetReleaseAllowListedAddressesCsvUrl($merkleRoot: String!) {
    merkleTreeCSVUrl(merkleRoot: $merkleRoot)
  }

  query ReleaseAllowlistModalData($id: UUID!) {
    release(id: $id) {
      id
      artist {
        id
        name
        user {
          id
          avatar {
            id
            url
          }
        }
      }
    }
  }
`);

export type ReleasesAllowlist = NonNullable<
  NonNullable<
    NonNullable<ReleaseAllowlistSelectedReleasesQuery['releaseAllowlist']>['selectedReleaseSources']
  >['edges'][number]['node']['sourceRelease']
>;

export type ArtistsAllowlist = NonNullable<
  NonNullable<
    NonNullable<ReleaseAllowlistSelectedArtistsQuery['releaseAllowlist']>['selectedArtistSources']
  >['edges'][number]['node']['sourceArtist']
>;

const initialPaginationArgs: CursorConnectionArgs = {
  first: 5,
  after: null,
};

const mintType = MintingAccessConfigMintingType.Presale;

export const ReleasesAllowlistModal = ({
  releaseId,
  merkleRoot,
}: {
  releaseId: string;
  merkleRoot: string | null;
}) => {
  const { closeCancelModal } = ModalContext.useContainer();
  const { tablet } = MediaQuery.useContainer();
  const [releasesAllowlist, setReleasesAllowlist] = useState<ReleasesAllowlist[]>([]);
  const [artistsAllowlist, setArtistsAllowlist] = useState<ArtistsAllowlist[]>([]);
  const [paginationReleases, setPaginationReleases] = useState(initialPaginationArgs);
  const [paginationArtists, setPaginationArtists] = useState(initialPaginationArgs);

  const { data: releaseData } = useQuery(ReleaseAllowlistModalDataDocument, {
    variables: !!releaseId && { id: releaseId },
    staleTime: 0,
  });

  const { data: releasesData, isLoading: releaseDataLoading } = useQuery(
    ReleaseAllowlistSelectedReleasesDocument,
    {
      variables: !!merkleRoot && {
        input: {
          releaseId,
          merkleRoot,
        },
        pagination: paginationReleases,
      },
      staleTime: 0,
    },
  );

  const { data: artistsData, isLoading: artistDataLoading } = useQuery(
    ReleaseAllowlistSelectedArtistsDocument,
    {
      variables: !!merkleRoot && {
        input: {
          releaseId,
          merkleRoot,
        },
        pagination: paginationArtists,
      },
      staleTime: 0,
    },
  );

  const { data: baseAllowlistData, isLoading: manuallyAddedAddressesLoading } = useQuery(
    BaseReleaseAllowlistDocument,
    {
      variables: !!merkleRoot && {
        input: {
          releaseId,
          merkleRoot,
        },
      },
      staleTime: 0,
    },
  );

  const manuallyAddedAddresses = baseAllowlistData?.data.releaseAllowlist?.manuallyAddedAddresses;

  const { data: allowlistCSV } = useQuery(GetReleaseAllowListedAddressesCsvUrlDocument, {
    variables: !!merkleRoot && { merkleRoot },
    staleTime: 0,
  });

  const csvUrl = allowlistCSV?.data.merkleTreeCSVUrl;

  const isModalLoading = releaseDataLoading || artistDataLoading;

  const releasesInfo = releasesData?.data.releaseAllowlist?.selectedReleaseSources;

  // Releases
  useEffect(() => {
    if (!releasesInfo) return;

    setReleasesAllowlist(prev =>
      mergeUniqLists({
        current: prev,
        incoming: releasesInfo.edges.map(v => v.node.sourceRelease).filter(Boolean),
        uniq(node) {
          return node.id;
        },
      }),
    );
  }, [releasesInfo]);

  const artistsInfo = artistsData?.data.releaseAllowlist?.selectedArtistSources;

  const totalUsers = baseAllowlistData?.data.releaseAllowlist?.totalUsers;

  // Artists
  useEffect(() => {
    if (!artistsInfo) return;

    setArtistsAllowlist(prev =>
      mergeUniqLists({
        current: prev,
        incoming: artistsInfo.edges.map(v => v.node.sourceArtist).filter(Boolean),
        uniq(node) {
          return node.id;
        },
      }),
    );
  }, [artistsInfo]);

  const pageInfoReleases = releasesInfo?.pageInfo;
  const pageInfoArtists = artistsInfo?.pageInfo;

  const hasNextReleases = !!pageInfoReleases?.hasNextPage;
  const nextCursorReleases = pageInfoReleases?.endCursor;

  const hasNextArtists = !!pageInfoArtists?.hasNextPage;
  const nextCursorArtists = pageInfoArtists?.endCursor;

  const loadMoreReleases = () => {
    if (hasNextReleases && nextCursorReleases) {
      setPaginationReleases({ after: nextCursorReleases, first: 3 });
    }
  };

  const loadMoreArtists = () => {
    if (hasNextArtists && nextCursorArtists) {
      if (hasNextArtists && nextCursorArtists) {
        setPaginationArtists({ after: nextCursorArtists, first: 3 });
      }
    }
  };

  const allowlistDescription =
    manuallyAddedAddresses?.description || 'Addresses selected by artist';

  const createdAt = baseAllowlistData?.data.releaseAllowlist?.createdAt;

  const date = !!createdAt
    ? `${format(new Date(createdAt), 'MMM')} ${format(new Date(createdAt), 'do')}, ${format(
        new Date(createdAt),
        'YYY',
      )}`
    : '';

  const time = !!createdAt
    ? new Date(createdAt).toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      })
    : '';

  const createdAtFormatted = `${date}, ${time}`;

  const allArtistCollaborations =
    baseAllowlistData?.data.releaseAllowlist?.info.allArtistCollaborations;
  const releasesAllowlistLength = releasesAllowlist.length + (allArtistCollaborations ? 1 : 0);

  const allArtistCollectors = baseAllowlistData?.data.releaseAllowlist?.info.allArtistCollectors;
  const artistsAllowlistLength = artistsAllowlist.length + (allArtistCollectors ? 1 : 0);

  const artistName = releaseData?.data.release?.artist?.name;
  const avatarUrl = releaseData?.data.release?.artist.user?.avatar?.url;

  const body = (
    <Sections>
      <AddressDetails>
        <p>
          {totalUsers} addresses eligible based on a snapshot taken on {createdAtFormatted}
        </p>
        <AccessibleLink
          absoluteUri={csvUrl ?? ''}
          download="allowlist.csv"
          disabled={!csvUrl}
          type="external"
        >
          <Button
            label="Download CSV"
            leadingIcon={<FontAwesomeIcon icon={faDownload} size="lg" />}
            variant="text"
            size="M"
            deprecatedCSS={{ width: '129px' }}
            type="button"
            loading={isModalLoading}
            disabled={!csvUrl}
          />
        </AccessibleLink>
      </AddressDetails>
      <Divider orientation="horizontal" css={{ marginBottom: 0, marginTop: 16 }} />
      {artistDataLoading ? (
        <Section>
          <DeprecatedDynamicSkeleton width={150} height={10} borderRadius={8} />
          {Array.from({ length: 5 }).map((_, i) => {
            return <ArtistRowSkeleton key={i} />;
          })}
        </Section>
      ) : (
        artistsAllowlistLength >= 1 && (
          <Section>
            <SectionHeader>
              <p>{SELECTED_ARTISTS_COLLECTORS_HEADER}</p>
            </SectionHeader>
            <CollectorRows>
              {allArtistCollectors && !!artistName && !!avatarUrl && (
                <AllArtistCollectorsRow artistName={artistName} avatarUrl={avatarUrl} />
              )}
              {artistsAllowlist.map(artist => (
                <Row key={artist.id} center>
                  <Avatar
                    size={40}
                    walletAddress={artist.user.publicAddress}
                    webappUri={artist.webappUri}
                    src={artist.user?.avatar?.url}
                  />
                  <CollectorAndReleaseDetails>
                    <RowTitle>{artist.user.username}</RowTitle>
                  </CollectorAndReleaseDetails>
                </Row>
              ))}
            </CollectorRows>
            {artistsAllowlistLength >= 5 && hasNextArtists && (
              <Button
                label="Load more"
                variant="tertiary"
                size="M"
                onClick={loadMoreArtists}
                className="loadMore"
                disabled={!hasNextArtists}
                loading={artistDataLoading}
              />
            )}
          </Section>
        )
      )}
      {releaseDataLoading ? (
        <Section>
          <DeprecatedDynamicSkeleton width={150} height={10} borderRadius={8} />
          {Array.from({ length: 5 }).map((_, i) => {
            return <ReleaseRowSkeleton key={i} />;
          })}
        </Section>
      ) : (
        releasesAllowlistLength >= 1 && (
          <Section>
            <SectionHeader>
              <p>{SELECTED_RELEASES_COLLECTORS_HEADER}</p>
            </SectionHeader>
            <CollectorRows>
              {allArtistCollaborations && !!artistName && (
                <AllArtistCollaborationsRow artistName={artistName} />
              )}
              {releasesAllowlist.map(release => (
                <Row key={release.id}>
                  <Cover
                    size={40}
                    src={getStaticCoverImage({
                      coverImage: release.coverImage.url,
                      staticCoverImage: release.staticCoverImage?.url,
                    })}
                    alt={release?.title}
                  />
                  <CollectorAndReleaseDetails>
                    <RowTitle>{release?.title}</RowTitle>
                    <RowSubTitle>{release?.artist.name}</RowSubTitle>
                  </CollectorAndReleaseDetails>
                </Row>
              ))}
            </CollectorRows>
            {releasesAllowlistLength >= 5 && hasNextReleases && (
              <Button
                label="Load more"
                variant="tertiary"
                size="M"
                onClick={loadMoreReleases}
                className="loadMore"
                disabled={!hasNextReleases}
                loading={releaseDataLoading}
              />
            )}
          </Section>
        )
      )}
      {manuallyAddedAddressesLoading ? (
        <Section manuallyAdded>
          <Header>
            <DeprecatedDynamicSkeleton width={150} height={10} borderRadius={8} />
            <DeprecatedDynamicSkeleton width={150} height={10} borderRadius={8} />
          </Header>
        </Section>
      ) : (
        !!manuallyAddedAddresses &&
        manuallyAddedAddresses.totalAddresses > 0 && (
          <Section manuallyAdded>
            <Header>
              <SectionHeader>
                <p>{MANUALLY_ADDED_ADDRESSES_HEADER}</p>
              </SectionHeader>
              <SubText>{allowlistDescription}</SubText>
            </Header>
          </Section>
        )
      )}
    </Sections>
  );

  const allowlistType = getAllowlistDisplayName({ mintType });

  return (
    <StandardModal
      title={`${allowlistType} Allowlist`}
      body={body}
      closeModal={closeCancelModal}
      isFullScreen={!tablet}
    />
  );
};

const AddressDetails = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});
